import KeyboardProfileItem    from './Items/KeyboardProfileItem';
import CollectionList         from '@/js/Shared/Collections/CollectionList';

export {KeyboardControllerSingleton as default}

/**
 * Keyboard Controller
 *
 * Manages all keyboard key press events
 *
 * @author      rsimoes <ruben-r-simoes@telecom.pt>
 * @copyright   Copyright (C) Sapo. All rights reserved.
 * @license     Private
 * @since       7.0.0
 */
class KeyboardController {
    /**
     * Defines all javascript meta keys names
     *
     * @private
     *
     * @param {String}
     *
     * @since 7.0.0
     */
    META_KEYS = ['ctrlKey', 'shiftKey', 'altKey', 'metaKey'];

    /**
     * Holds all registered keyboard profiles
     *
     * @private
     *
     * @param {CollectionList}
     *
     * @since 7.0.0
     */
    profiles = new CollectionList('name');

    /**
     * Holds active shortcuts
     *
     * @private
     *
     * @param {CollectionList}
     *
     * @since 7.0.0
     */
    shortcuts = new CollectionList('id');

    /**
     * Registers keyboard window event listeners
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    addEventListeners() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup'  , this.onKeyUp);
    }

    /**
     * Removes keyboard window event listeners
     *
     * Stops broadcasting previous registered drag events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    removeEventListeners() {
        window.removeEventListener('keydown', KeyboardControllerSingleton.onKeyDown);
        window.removeEventListener('keyup'  , KeyboardControllerSingleton.onKeyUp);
    }

    /**
     * Called every time a window keyup event occurs
     *
     * @private
     *
     * @param {KeyboardEvent} event
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onKeyUp(event) {
        let pressed_id = KeyboardControllerSingleton.getPressedKeysId(event);
        let shortcut   = KeyboardControllerSingleton.shortcuts.index[pressed_id];

        if (!shortcut || !shortcut.interval || shortcut.blocked === false) {
            return;
        }

        shortcut.clearBlockedTimeout();
    }

    /**
     * Called every time a window keydown event occurs
     *
     * @private
     *
     * @param {KeyboardEvent} event
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onKeyDown(event) {
        let pressed_id = KeyboardControllerSingleton.getPressedKeysId(event);
        let shortcut   = KeyboardControllerSingleton.shortcuts.index[pressed_id];

        if (!shortcut || shortcut.blocked === true || !shortcut.callback) {
            return;
        }

        if (shortcut.delay) {
            shortcut.setBlockedTimeout();
        }

        event.preventDefault();
        shortcut.callback();
    }

    /**
     * Creates a new set of keyboard shortcuts associated with a profile
     *
     * Registering a profile with the same id replaces the previous profile
     *
     * @see {KeyboardProfiles} for all available keyboard profile configurations
     *
     * @public
     *
     * @param {Object}                 profile        keyboard profile to be created
     * @param {String}                 profile.name   profile name
     * @param {Object}                 profile.config profile configuration
     * @param {Object<string,function} callbacks      an Object with all shortcuts actions callbacks {action_name: callback}
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    create(profile, callbacks) {
        let profile_item    = new KeyboardProfileItem();
        profile_item.name   = profile.name;
        profile_item.config = profile.config;

        profile_item.addShortcuts(callbacks);
        this.profiles.add(profile_item);
        this.setActiveShortcuts();
    }

    /**
     * Deletes a registered profile and all it's shortcuts
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Object} profile profile to be deleted
     *
     * @return {void}
     */
    delete(profile) {
        this.profiles.delete(profile.name);
        this.setActiveShortcuts();
    }

    /**
     * Disables a given profile
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Object} profile profile to be disabled
     *
     * @return {void}
     */
    disable(profile) {
        if (!this.profiles.index[profile.name]) {
            return;
        }

        this.profiles.index[profile.name].enabled = false;
        this.setActiveShortcuts();
    }

    /**
     * Enables a given profile
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Object} profile profile to be enabled
     *
     * @return {void}
     */
    enable(profile) {
        if (!this.profiles.index[profile.name]) {
            return;
        }

        this.profiles.index[profile.name].enabled = true;
        this.setActiveShortcuts();
    }

    /**
     * Disables all registered profiles
     *
     * Profiles set after this method are not affected, and are enabled by default
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    disableAll() {
        this.profiles.items.forEach((profile) => {
            profile.enabled = false;
        });

        this.setActiveShortcuts();
    }

    /**
     * Enables all registered profiles
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    enableAll() {
        this.profiles.items.forEach((profile) => {
            profile.enabled = true;
        });

        this.setActiveShortcuts();
    }

    /**
     * Rebuilds the list of shortcuts based on the active profiles
     *
     * @private
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    setActiveShortcuts() {
        this.shortcuts.reset();

        this.profiles.items.forEach((profile) => {
            if (profile.enabled === false) {
                return;
            }

            profile.shortcuts.items.forEach((shortcut) => {
                this.shortcuts.add(shortcut);
            })
        })
    }

    /**
     * Analyzes the keyboard event object and returs the shortcut id
     * based on the pressed key
     *
     * @private
     *
     * @param {KeyboardEvent} event
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    getPressedKeysId(event) {
        let id = `${event.key}`;

        this.META_KEYS.forEach(meta => {
            if (event[meta]) {
                id += `+${meta}`;
            }
        });

        return id;
    }
}

const KeyboardControllerSingleton = new KeyboardController();
